import { ChangeDetectionStrategy, Component, type DoCheck, inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FieldType, FormlyFormBuilder, type FormlyFieldConfig } from '@ngx-formly/core';

import { DETAILS_SERVICE_TOKEN } from '../../../../features/admin/modules/modules-entity/templates/details/details/details.service';
import { OverviewCardComponent } from './overview-card.component';
import { ReadSectionEmptyComponent } from './read-section-empty.component';
import { readonlyPresenceValue } from './readonly.types';
import { isOverviewVisible, overviewAncestorsVisible } from './overview.types';
import type { OverviewRow, OverviewSection } from './overview.types';

/** Jump link on a card head; the chevron is part of the label, matching the design language. */
const SEE_ALL_LABEL = 'Bekijk alles ›';

/**
 * The rows of an own-view card: none, ever — it IS the field. Module-level and frozen in identity
 * so the card's OnPush `[rows]` input does not change every check; the per-check cache below is
 * cleared each `ngDoCheck` and would hand out a fresh `[]` every time.
 */
const NO_ROWS: OverviewRow[] = Object.freeze([]) as OverviewRow[];

/**
 * The synthetic Overzicht tab of the read view (#21239, task 21250).
 *
 * Never configured by an admin and never offered in the form-builder type picker: `DetailsService`
 * builds this node itself when the module opted in, and registers it in `INTERNAL_INPUTS`
 * (`formly-fields/index.ts`). It renders one `dd-overview-card` per section, each holding the
 * flagged answers of one tab/page.
 *
 * A row's VALUE is read here, at render time, not baked into the section: the section carries the
 * very field objects Formly is building, and their `formControl` only exists once that build has
 * run. Reading it in `DetailsService.fields()` — which runs before — would print `undefined` for
 * every answer.
 */
@Component({
  selector: 'formly-field-overview-tab',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [OverviewCardComponent, ReadSectionEmptyComponent],
  template: `
    <!-- The same cards in every mode (decision 2026-09-16). They used to be recessed and inert on
         the edit face, under a banner saying Overzicht was read-view-only — but a summary of what
         you are editing is worth reading, and its "Bekijk alles" links are worth following. What
         the edit face needs is only that it does not OPEN here, which is what the node's
         skipInitialSelection flag says (see addOverviewTab in details.service.ts). -->
    <div class="flex flex-col gap-4">
      @for (section of visibleSections(); track section) {
        <dd-overview-card
          [label]="section.label"
          [sectionKey]="cardKeyOf(section)"
          [rows]="rowsOf(section)"
          [ownViewField]="ownViewFieldOf(section)"
          [seeAllLabel]="seeAllLabelFor(section)"
          (seeAll)="goToSection(section)" />
      } @empty {
        <!-- Every flagged answer role-hidden, or (the common case) every flagged optional answer
             empty and hidden by readonlyExtension: the tab must not land the reader on a blank
             page. Same note as any other empty read section. -->
        <dd-read-section-empty />
      }
    </div>
  `,
})
export class OverviewTabComponent extends FieldType implements DoCheck {
  /**
   * Optional, like every other field that talks to the detail page: this type also renders in
   * Storybook and in the admin form preview, neither of which provides the service.
   */
  readonly #detailsService = inject(DETAILS_SERVICE_TOKEN, { optional: true });

  /**
   * Per-check memo for {@link rowsOf} (review finding): the template calls `rowsOf(section)`
   * twice per section on every check — once from `visibleSections()`'s length filter, once from
   * the `[rows]` binding — which re-filtered and re-mapped the same section twice. Cleared in
   * {@link ngDoCheck}, which Angular runs once per check regardless of OnPush, so the cache never
   * outlives the check it was built for and a live value written between two checks (Formly
   * expressions, `readonlyPresenceValue` reading the live control or an upload's entry documents)
   * still surfaces on the next one — this only removes the DUPLICATE work within a single check,
   * not any recomputation.
   */
  readonly #rowsCache = new Map<OverviewSection, OverviewRow[]>();

  /** Builds the edit face's own-view CLONES into real Formly fields — see {@link ngDoCheck}. */
  readonly #formlyFormBuilder = inject(FormlyFormBuilder);
  /** The clones this instance already built; a WeakSet so a rebuilt tree's old clones can be collected. */
  readonly #builtClones = new WeakSet<FormlyFieldConfig>();

  /**
   * Two jobs, both tied to the check cycle rather than to a signal:
   * - clear the per-check {@link rowsOf} memo (see `#rowsCache`);
   * - build every own-view CLONE this tab is about to mount. `cloneOwnViewFieldReadonly`
   *   (overview.types.ts) hands the edit face a keyless, UNBUILT clone, and `<formly-field>`
   *   refuses to render a field that never went through `FormlyFormBuilder`. The build happens
   *   HERE, in a lifecycle hook of the component that mounts the card, and not in
   *   `DetailsService.fields()` where the clone is made: `buildForm` evaluates the clone's
   *   expressions synchronously (user roles, form state), which a computed must neither track nor
   *   hide behind `untracked`. Angular runs lifecycle hooks with no active reactive consumer, so
   *   nothing read during the build becomes a dependency of anything. `ngDoCheck` (not `ngOnInit`)
   *   so a section list that changes under a reused instance still gets its new clones built
   *   before the template binds them; the WeakSet keeps every clone at exactly one build.
   *
   * The build uses a FRESH `FormGroup`/model — never the live tab's own form — and the SAME
   * `options.formState` OBJECT the live tree carries (`this.options` is the root options
   * `<formly-form>` built, and `DetailsService`'s constructor effect keeps mutating that one
   * object in place via `Object.assign`), so the clone sees the entry the moment the live tree
   * does, including on a hard reload of `/…/bewerken` where the entry resolves after the tree.
   */
  ngDoCheck(): void {
    this.#rowsCache.clear();
    this.#buildPendingClones();
  }

  #buildPendingClones(): void {
    for (const section of this.sections()) {
      if (section.kind !== 'own-view' || !section.isClone || this.#builtClones.has(section.field)) {
        continue;
      }
      this.#builtClones.add(section.field);
      this.#formlyFormBuilder.buildForm(new FormGroup({}), [section.field], {}, { formState: this.options?.formState });
    }
  }

  sections(): OverviewSection[] {
    return (this.props['sections'] as OverviewSection[]) ?? [];
  }

  /**
   * The sections that this reader may actually see, with at least one row left to show.
   *
   * Both filters run HERE rather than in `buildOverviewSections`, because both of the things they
   * read (`hide`, `className`) are written by Formly expressions that have not run yet when the
   * sections are built — see {@link isOverviewVisible}. A tab hidden for this role would otherwise
   * be summarised on a card the role is not allowed to open, and a tab whose every flagged answer
   * is hidden would render as an empty card.
   */
  visibleSections(): OverviewSection[] {
    return this.sections().filter(section => isOverviewVisible(section.source) && this.#hasSomethingToShow(section));
  }

  /**
   * A rows card needs at least one row left after the filters; an own-view card needs its own
   * field to survive the same two questions. Both are asked of the field, not of the section,
   * because both switches are written by expressions that run after the sections were built — the
   * reason the whole filter lives here rather than in `buildOverviewSections`.
   */
  #hasSomethingToShow(section: OverviewSection): boolean {
    if (section.kind === 'own-view') {
      // `sourceField`, never `field`: on the edit face `field` is a clone built against its own
      // isolated mini-tree (`#buildPendingClones` above), whose `.parent` chain never
      // reaches the live tab tree at all — walking it from `field` would never find a hidden
      // ancestor, so a checklist inside a hidden group would render its card anyway. `sourceField`
      // is the live object, whose `.parent` chain is the real one by the time this runs (render
      // time, after `<formly-form>` has built the actual tab tree).
      return isOverviewVisible(section.sourceField) && overviewAncestorsVisible(section.sourceField, section.source);
    }
    return this.rowsOf(section).length > 0;
  }

  /** The field an own-view card mounts, or `null` for an ordinary rows card. */
  ownViewFieldOf(section: OverviewSection): FormlyFieldConfig | null {
    return section.kind === 'own-view' ? section.field : null;
  }

  /**
   * Test hook only (`data-cy`), deliberately NOT `section.key`: a tab holding both ordinary
   * flagged answers and a flagged own-view field yields two cards that share that key, and two
   * elements with the same `data-cy` make an e2e selector pick an arbitrary one. The jump link
   * keeps using the real key — see {@link goToSection}.
   *
   * Reads `sourceField.key`, never `field.key`: on the edit face `field` is a clone whose key was
   * deliberately stripped (`cloneOwnViewFieldReadonly`), so reading it there always produced the
   * literal string `"undefined"` — every own-view card on the same tab collided on the same
   * `data-cy` instead of getting one each. `sourceField` is the original, still-keyed field.
   */
  cardKeyOf(section: OverviewSection): string {
    if (section.kind !== 'own-view') {
      return section.key;
    }
    // An own-view section always comes from `isAnswerField`, which requires a key — no fallback.
    return [section.key, String(section.sourceField.key)].filter(Boolean).join('-');
  }

  /**
   * A method, not a computed signal: `props`/`hide` are plain objects Formly mutates in place, so
   * a signal would never see the write. Formly marks the field view for check after each
   * expression pass, which is what re-runs this.
   *
   * Fields the read view hid are skipped for the same reason they were hidden on their own tab
   * (task 21245): an empty OPTIONAL answer is noise. A required-but-empty one keeps its `hide`
   * false and reaches the row, where it shows the "Ontbreekt" badge. A field the role may not see
   * at all (`className: '… hidden'`) is skipped for a harder reason: repeating it here would be a
   * leak.
   *
   * The same two questions are asked of every GROUP the answer sits in, up to this section
   * (`overviewAncestorsVisible`): a hidden block does not mark the fields inside it as hidden, so
   * without that walk its answers reappear here one card over.
   */
  rowsOf(section: OverviewSection): OverviewRow[] {
    const cached = this.#rowsCache.get(section);
    if (cached) {
      return cached;
    }
    if (section.kind === 'own-view') {
      return NO_ROWS;
    }
    // The same read row `ReadonlyWrapper` mounts on the field's own tab, so it must read the
    // same presence value — an `upload` field keeps its files on the entry, not in its control
    // (task 21243, defect C1).
    const rows = section.fields
      .filter(field => isOverviewVisible(field) && overviewAncestorsVisible(field, section.source))
      .map(field => ({ field, value: readonlyPresenceValue(field) }));
    this.#rowsCache.set(section, rows);
    return rows;
  }

  /**
   * The link is only offered where it can actually do something.
   *
   * `switchTab$` is the detail page's ONE inbound channel for section switching, and both
   * containers listen to it: `tabs.component.ts` has always done so, `pages.component.ts` since
   * fix round 1 of this task. The fallback section of a form with neither container has nowhere
   * to jump to and keeps its link suppressed.
   */
  seeAllLabelFor(section: OverviewSection): string | null {
    return section.key && this.#canSwitchSections() ? SEE_ALL_LABEL : null;
  }

  goToSection(section: OverviewSection): void {
    if (this.seeAllLabelFor(section)) {
      this.#detailsService!.switchTab$.next(section.key);
    }
  }

  /** The container this synthetic node was prepended into — `tabs`, `pages`, or nothing. */
  #canSwitchSections(): boolean {
    const parent = this.field.parent as FormlyFieldConfig | undefined;
    return !!this.#detailsService && (parent?.type === 'tabs' || parent?.type === 'pages');
  }
}
